import _t from 'i18n!nls/enterprise-admin-messaging-v2';

/**
 * Identifier for each pre-canned AI prompt the admin can pick from. Used both
 * as the key into `AI_MESSAGE_ACTIONS` and as the `?messageAction=<KEY>` URL
 * param value used by external entry points (wired in a follow-up PR).
 */
export type AIMessageActionKey =
  | 'REMIND_TO_JOIN'
  | 'MESSAGE_INACTIVE_LEARNERS'
  | 'MESSAGE_ENROLLED_LEARNERS'
  | 'SEND_ACCOLADES'
  | 'REMINDER_TO_COMPLETE_COURSE'
  | 'ENCOURAGE_ONGOING_LEARNING'
  | 'PROMOTE_POPULAR_COURSES'
  | 'REMIND_UPSKILLING_BENEFITS'
  | 'CAREER_OPPORTUNITIES';

export type AIMessageAction = {
  /**
   * Translated prompt text. Implemented as a getter so `_t()` is evaluated
   * at access time (locale changes propagate without restart).
   */
  readonly prompt: string;
};

/**
 * Centralised registry of the AI-generated message prompts surfaced across
 * the admin app. Adding a new entry here makes it linkable as
 * `?messageAction=<KEY>` from any page once the URL listener ships.
 */
export const AI_MESSAGE_ACTIONS: Record<AIMessageActionKey, AIMessageAction> = {
  REMIND_TO_JOIN: {
    get prompt() {
      return _t('Remind invited people to join Coursera in the last 30 days');
    },
  },
  MESSAGE_INACTIVE_LEARNERS: {
    get prompt() {
      return _t('Remind inactive learners in the last 7 days to complete their courses');
    },
  },
  MESSAGE_ENROLLED_LEARNERS: {
    get prompt() {
      return _t('Encourage learners enrolled in the last 30 days to continue making progress');
    },
  },
  SEND_ACCOLADES: {
    get prompt() {
      return _t('Congratulate learners who have completed a course this week');
    },
  },
  REMINDER_TO_COMPLETE_COURSE: {
    get prompt() {
      return _t('Remind learners to finish a course to complete it');
    },
  },
  ENCOURAGE_ONGOING_LEARNING: {
    get prompt() {
      return _t('Encourage all joined learners to keep learning for career or personal growth');
    },
  },
  PROMOTE_POPULAR_COURSES: {
    get prompt() {
      return _t('Send a message to encourage joined learners to explore our most popular courses');
    },
  },
  REMIND_UPSKILLING_BENEFITS: {
    get prompt() {
      return _t('Remind joined learners of the value and benefits of upskilling through Coursera');
    },
  },
  CAREER_OPPORTUNITIES: {
    get prompt() {
      return _t('Reinforce how ongoing learning can unlock new career opportunities and growth');
    },
  },
};

/**
 * Prompt suggestion chips shown inside the Define-goal screen, in the order
 * they appear in the design mock.
 */
export const DEFINE_GOAL_SUGGESTIONS: AIMessageActionKey[] = [
  'MESSAGE_INACTIVE_LEARNERS',
  'SEND_ACCOLADES',
  'REMIND_TO_JOIN',
  'MESSAGE_ENROLLED_LEARNERS',
];
